import React, { useState } from "react";
import { useLang } from "./LanguageProvider";
import { Check, ArrowUpRight } from "lucide-react";
import Lightbox from "./Lightbox";

const FACTORY_GALLERY = [
  { src: "/factory/exterior.jpg",      en: "Plant Exterior",                 ar: "المبنى الخارجي للمصنع" },
  { src: "/factory/warehouse.jpg",     en: "Raw Material Storage",           ar: "تخزين المواد الخام" },
  { src: "/factory/extruder-1.jpg",    en: "Extrusion Line · CPVC Pipe",     ar: "خط بثق أنابيب CPVC" },
  { src: "/factory/extruder-2.jpg",    en: "Theysohn Extruder Operation",    ar: "تشغيل خط بثق Theysohn" },
  { src: "/factory/extruder-3.jpg",    en: "Continuous Extrusion",           ar: "البثق المستمر" },
  { src: "/factory/engel-moulding.jpg",en: "Engel Injection Moulding",       ar: "حقن إنجل للأشكال" },
  { src: "/factory/piovan-feeder.jpg", en: "Piovan Material Feeder",         ar: "وحدة تغذية Piovan" },
  { src: "/factory/mold-storage.jpg",  en: "Tooling & Mould Library",        ar: "مكتبة القوالب" },
  { src: "/factory/testlab-1.jpg",     en: "Quality Control Laboratory",     ar: "مختبر مراقبة الجودة" },
  { src: "/factory/testlab-2.jpg",     en: "Hydrostatic Testing",            ar: "اختبار الضغط الهيدروستاتيكي" },
  { src: "/factory/testlab-3.jpg",     en: "Vicat Softening Point Tester",   ar: "جهاز اختبار نقطة الليونة" },
];

export default function Factory() {
  const { t, lang, isRtl } = useLang();
  const [lbIdx, setLbIdx] = useState(null);

  const items = FACTORY_GALLERY.map((g) => ({
    src: g.src,
    caption: lang === "ar" ? g.ar : g.en,
    subcaption: lang === "ar" ? "مصنع FlowWhite · الدوحة" : "FlowWhite Factory · Doha, Qatar",
  }));

  return (
    <section
      id="factory"
      data-testid="factory-section"
      className="relative bg-brand-navyDeep py-24 md:py-40 overflow-hidden"
    >
      <div className="container mx-auto px-6 md:px-10">
        {/* Header */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 mb-16 md:mb-20">
          <div className="lg:col-span-6 reveal">
            <div className="flex items-center gap-3 mb-6">
              <span className="h-px w-8 bg-brand-teal" />
              <span className="text-[11px] uppercase tracking-[0.28em] text-brand-teal font-semibold">
                {t.factory.overline}
              </span>
            </div>
            <h2 className="font-heading text-white text-2xl sm:text-3xl md:text-4xl lg:text-[44px] leading-[1.15] font-medium tracking-tight">
              {t.factory.title}
            </h2>
          </div>
          <div className="lg:col-span-6 reveal reveal-delay-1">
            <div className={`space-y-4 text-base md:text-lg text-white/65 leading-relaxed ${isRtl ? "text-right" : "text-left"}`}>
              {t.factory.body.split("\n\n").map((para, i) => (
                <p key={i} data-testid={`factory-body-${i}`}>{para}</p>
              ))}
            </div>
            <ul className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
              {t.factory.bullets.map((b, i) => (
                <li
                  key={i}
                  data-testid={`factory-bullet-${i}`}
                  className="flex items-start gap-3 text-white/80 text-sm leading-relaxed"
                >
                  <span className="mt-1 inline-flex items-center justify-center w-4 h-4 rounded-full bg-brand-teal/15 text-brand-teal flex-shrink-0">
                    <Check size={10} strokeWidth={2.5} />
                  </span>
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Editorial bento gallery — real factory photos */}
        <div className="grid grid-cols-12 gap-3 md:gap-5 auto-rows-[200px] md:auto-rows-[260px]">
          {/* Big lead image */}
          <FactoryTile
            i={0}
            className="col-span-12 md:col-span-7 row-span-2"
            item={items[1]} // warehouse
            tag={isRtl ? "01 · تخزين" : "01 · Storage"}
            onOpen={() => setLbIdx(1)}
          />
          <FactoryTile
            i={1}
            className="col-span-6 md:col-span-5"
            item={items[5]} // engel
            tag={isRtl ? "02 · حقن" : "02 · Moulding"}
            onOpen={() => setLbIdx(5)}
          />
          <FactoryTile
            i={2}
            className="col-span-6 md:col-span-5"
            item={items[2]} // extruder-1
            tag={isRtl ? "03 · بثق" : "03 · Extrusion"}
            onOpen={() => setLbIdx(2)}
          />

          <FactoryTile
            i={3}
            className="col-span-6 md:col-span-4"
            item={items[8]} // testlab-1
            tag={isRtl ? "04 · مختبر" : "04 · QC Lab"}
            onOpen={() => setLbIdx(8)}
          />
          <FactoryTile
            i={4}
            className="col-span-6 md:col-span-4"
            item={items[7]} // mold-storage
            tag={isRtl ? "05 · قوالب" : "05 · Tooling"}
            onOpen={() => setLbIdx(7)}
          />
          <FactoryTile
            i={5}
            className="col-span-12 md:col-span-4"
            item={items[6]} // piovan-feeder
            tag={isRtl ? "06 · تغذية" : "06 · Feeder"}
            onOpen={() => setLbIdx(6)}
          />
        </div>

        {/* View all CTA */}
        <div className="mt-8 flex justify-end">
          <button
            onClick={() => setLbIdx(0)}
            data-testid="factory-view-all"
            className="group inline-flex items-center gap-2 text-sm font-medium text-white/75 hover:text-white border-b border-white/15 hover:border-brand-teal pb-1 transition-colors"
          >
            {isRtl ? `استعراض ${items.length} صورة من المصنع` : `View all ${items.length} factory photos`}
            <ArrowUpRight size={14} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </button>
        </div>

        {/* Spec strip */}
        <div className="reveal reveal-delay-2 mt-14 grid grid-cols-2 md:grid-cols-4 gap-px bg-white/10 rounded-xl overflow-hidden">
          {[
            { k: "6,000", l: isRtl ? "م² مساحة" : "m² floor area" },
            { k: "2015", l: isRtl ? "سنة التأسيس" : "established" },
            { k: "ISO", l: isRtl ? "9001:2015" : "9001:2015 certified" },
            { k: "16–225", l: isRtl ? "ملم نطاق" : "mm pipe range" },
          ].map((s, i) => (
            <div
              key={i}
              className="bg-brand-navyDeep p-7 md:p-9"
              data-testid={`factory-spec-${i}`}
            >
              <div dir="ltr" className="display-num text-white text-3xl md:text-4xl">{s.k}</div>
              <div className="text-[10px] md:text-xs uppercase tracking-[0.18em] text-white/45 mt-2">
                {s.l}
              </div>
            </div>
          ))}
        </div>
      </div>

      <Lightbox
        items={items}
        index={lbIdx}
        onClose={() => setLbIdx(null)}
        onPrev={() => setLbIdx((i) => (i - 1 + items.length) % items.length)}
        onNext={() => setLbIdx((i) => (i + 1) % items.length)}
      />
    </section>
  );
}

function FactoryTile({ className, item, tag, onOpen, i }) {
  return (
    <button
      type="button"
      onClick={onOpen}
      data-testid={`factory-tile-${i}`}
      className={`reveal group relative overflow-hidden rounded-xl bg-brand-surface text-left ${className}`}
      style={{ transitionDelay: `${(i % 5) * 60}ms` }}
    >
      <img
        src={item.src}
        alt={item.caption}
        loading="lazy"
        className="absolute inset-0 w-full h-full object-cover transition-transform [transition-duration:1600ms] group-hover:scale-110"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-brand-navyDarker/95 via-brand-navyDeep/35 to-transparent" />
      <div className="absolute inset-x-0 bottom-0 p-5 md:p-6 flex items-end justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-brand-teal font-semibold">
            {tag}
          </div>
          <h3 className="mt-1.5 text-white text-base md:text-lg font-medium leading-tight">
            {item.caption}
          </h3>
        </div>
        <span className="opacity-0 group-hover:opacity-100 translate-y-1 group-hover:translate-y-0 transition-all duration-300 inline-flex items-center justify-center w-9 h-9 rounded-full border border-white/30 bg-white/[0.06] text-white">
          <ArrowUpRight size={14} />
        </span>
      </div>
    </button>
  );
}
